import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Read } from 'services/cobranca';
import { PerfilContent, StatusContent, Title } from 'ui/styled';
import { marcadorDiferente, normalizeResponse, selectColors } from 'utils';
import { parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function BankingRenegociacao() {
  const { user, setModal, filterExpression } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0);

  // Atualiza a cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 120000);
    return () => clearInterval(interval);
  }, []);

  const init = useCallback(async () => {
    setLoading(true);
    try {
      const result = await Read('?filters[status]=renegociar');
      const data = normalizeResponse(result);
      if (!data.length) {
        setRegisters([]);
        setLoading(false);
        return;
      }
      const sorted = data.sort((a, b) => (b.id || 0) - (a.id || 0));
      const next = sorted.map((item) => ({ ...item }));
      setRegisters(next);
    } catch (err) {
      console.error('Erro ao carregar propostas:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Id',
        ref: 'id',
        renderCell: ({ row }) => {
          //verifica se ja teve renegociacao.
          const origemRenegociado = row?.parcelas_origem;
          const renegociado = row.titulos
            .filter((a) => a.status === 'baixado')
            .map((a) => a.renegociar)
            .reduce((verdadeiro, renegociado) => {
              return renegociado === true ? true : false;
            }, null);

          // Pega o ID
          const cobrancaId = row?.id;
          if (origemRenegociado && renegociado) {
            return marcadorDiferente(cobrancaId, '! #');
          } else if (origemRenegociado) {
            return marcadorDiferente(cobrancaId, '!');
          } else if (renegociado) {
            return marcadorDiferente(cobrancaId, '#');
          }
          return cobrancaId;
        },
      },
      {
        title: 'Data',
        ref: 'date',
        sortable: true,
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row.date),
      },
      { title: 'Banco', ref: 'banco' },
      // Inserir coluna "Distribuidora" condicionalmente
      ...(user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
        ? [
            {
              title: 'Distribuidora',
              sortable: true,
              ref: 'distributor',
            },
          ]
        : []),
      { title: 'Integrador', ref: 'integrator' },
      { title: 'Cliente', ref: 'customer', sortable: true },
      {
        title: 'Telefone',
        ref: 'telefone',
        renderCell: ({ row }) => {
          if (!row?.telefone) return '-';
          const numeroLimpo = row.telefone.replace(/\D/g, '');
          const linkWhats = `https://wa.me/55${numeroLimpo}`;
          return (
            <a
              href={linkWhats}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'blue',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {row.telefone}
            </a>
          );
        },
      },
      {
        title: 'Perfil',
        ref: 'bureau_perfil',
        align: 'center',
        renderCell: ({ row }) => (
          <PerfilContent color={selectColors(row?.bureau_perfil)}>
            {row?.bureau_perfil}
          </PerfilContent>
        ),
      },
      { title: 'Taxa', ref: 'taxa' },
      { title: 'Valor do Contrato', ref: 'value' },
      { title: 'Saldo Devedor', ref: 'saldo' },
      {
        title: 'Parcelas',
        ref: 'parcelas',
        renderCell: ({ row }) => {
          //traz a qtd de parcelas que nao esteja baixada.
          const parcelas = row.titulos.filter((a) => a.status !== 'baixado');
          return parcelas.length;
        },
      },
      { title: 'PMT', ref: 'pmt' },
      {
        title: 'Ultimo Vencimento',
        ref: 'ultimo_vencimento',
        sortable: true,
        renderCell: ({ row }) => {
          //traz a data do vencimento da ultima parcela que nao esteja baixada.
          const dataVencida = row.titulos
            .filter((a) => a.status !== 'baixado')
            .map((a) => new Date(a.data_vencimento))
            .reduce((dataAntiga, atual) => {
              return !dataAntiga || atual < dataAntiga ? atual : dataAntiga;
            }, null);
          const resultado = formatadores.TIMESTAMP_TO_BR(dataVencida);
          return resultado;
        },
      },
      {
        title: 'Tag',
        ref: 'titulo',
        renderCell: ({ row }) => {
          let nomecampo;
          if (row.contato_cliente && row.confirma_cliente === false) {
            nomecampo = 'Aguardando Confirmação';
          }
          if (row.contato_cliente && row.confirma_cliente === true) {
            nomecampo = 'Confirmado com Cliente';
          }
          if (!row.contato_cliente) {
            nomecampo = 'Titulos';
          }
          return (
            <StatusContent
              color={selectColors(nomecampo)}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (!row.taxasiof || !row.taxasbanco) {
                  toast.info(
                    'Não localizado o cadastro das regras, verifique com o administrador'
                  );
                } else if (nomecampo === 'Aguardando Confirmação') {
                  toast.info('Aguardando confirmação do Cliente');
                } else {
                  setModal({
                    type: 'renegociar',
                    dados: row,
                    reload: () => init(),
                  });
                }
              }}
            >
              {nomecampo}
            </StatusContent>
          );
        },
      },
    ];
    return baseColumns;
  }, [user, init, setModal]);
  const rows = useMemo(() => {
    return (registers || []).map((a) => ({
      id: a?.id,
      date: a?.createdAt,
      formatedDate: formatadores.TIMESTAMP_TO_BR(a?.createdAt),
      cliente: a?.cliente,
      clienteid: a?.cliente?.id,
      distributor: normalizeName(a?.distribuidor?.trade_name) || '',
      distribuidorid: a?.distribuidor?.id,
      bankingid: !a?.banking ? '' : a?.banking.id,
      propostaid: !a?.proposta_financeira ? '' : a?.proposta_financeira.id,
      taxas: a?.distribuidor?.taxas,
      taxasbanco: a?.banco?.regras_cobranca,
      integrator:
        normalizeName(a?.proposta_financeira?.integrador?.nome_fantasia) || '',
      customer: normalizeName(a?.cliente?.nome_completo) || '',
      value: parseCurrency(a?.valor_financiado),
      bureau_perfil: !a?.banking
        ? '-'
        : !a?.banking.bureau_perfil
          ? '-'
          : a?.banking.bureau_perfil,
      taxa: `${a?.perc_juros}%`,
      seguro: a?.seguro,
      pmt: parseCurrency(a?.valor_pmt),
      saldo: parseCurrency(a?.valor_saldo),
      banco: normalizeName(a?.banco?.nome_fantasia),
      bancoid: a?.banco?.id,
      telefone: a?.cliente?.telefone_celular,
      status: a?.status,
      titulos: a?.titulos,
      perc_tac: a?.perc_tac,
      perc_iof: a?.perc_iof,
      valor_seguro_assistencia:
        a?.banco?.regras_cobranca?.valor_seguro_assistencia,
      renegociacao_taxa: a?.renegociacao_taxa,
      renegociacao_periodicidade: a?.renegociacao_periodicidade,
      renegociacao_prazo: a?.renegociacao_prazo,
      renegociacao_seguro: a?.renegociacao_seguro,
      renegociacao_seguro_assistencia: a?.renegociacao_seguro_assistencia,
      renegociacao_saldodevedor: a?.renegociacao_saldodevedor,
      renegociacao_iof: a?.renegociacao_iof,
      renegociacao_data_1parcela: a?.renegociacao_data_1parcela,
      renegociacao_entrada: a?.renegociacao_entrada,
      parcelas_origem: a?.parcelas_origem,
      carteira: a?.carteira,
      contato_cliente: a?.contato_cliente,
      confirma_cliente: a?.confirma_cliente,
      taxasiof: a?.distribuidor?.cobranca_regras_gerais,
      renegociacao_tab: a?.renegociacao_tab,
      renegociacao_tac: a?.renegociacao_tac,
    }));
  }, [registers]);

  useEffect(() => init(), [init, tick]);

  return (
    <ContainerAuthenticated>
      <Title>Banking</Title>
      <TableWrapper title="Renegociação" search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
        ! cobrança criada a partir de uma renegociação. <br /># cobrança que ja
        foram renegociadas.
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
